import { Link } from 'react-router-dom';
import { MapPin, Users, Sparkles } from 'lucide-react';
import Logo from '@components/ui/Logo';
import { AuthGradientBackground } from './AuthGradientBackground';

/** Still used by `/welcome` hero copy. */
export const WELCOME_HEADLINE = 'Welcome';
export const WELCOME_SUBLINE =
  'Discover vibes near you — sports, fitness, learning, and more. Log in or create an account to get started.';

const LOGIN_PANEL = {
  gradientVariant: 'desktopLogin',
  fallbackBg: '#4F46E5',
  title: (
    <>
      Do more,
      <br />
      together.
    </>
  ),
  body: 'Find people nearby who share your passion for sports, fitness, learning, and more.',
};

const REGISTER_PANEL = {
  gradientVariant: 'desktopRegister',
  fallbackBg: '#6D28D9',
  title: (
    <>
      Your next
      <br />
      adventure awaits.
    </>
  ),
  body: 'Join thousands of people discovering vibes and making new connections every day.',
};

const FEATURES = [
  { Icon: MapPin, text: 'Discover vibes happening around you' },
  { Icon: Users, text: 'Connect with like-minded people nearby' },
  { Icon: Sparkles, text: 'Create your own vibes and invite others' },
];

/**
 * Desktop-only left column for `/login` and `/register`.
 * @param {{ page: 'login' | 'register', className?: string }} props
 */
export const AuthWelcomeAside = ({ page = 'login', className = '' }) => {
  const config = page === 'register' ? REGISTER_PANEL : LOGIN_PANEL;

  return (
    <div
      className={`relative hidden min-h-screen w-full shrink-0 flex-col md:flex md:w-[45%] ${className}`.trim()}
      style={{ backgroundColor: config.fallbackBg }}
    >
      <AuthGradientBackground variant={config.gradientVariant} className="absolute inset-0 z-0 min-h-full" />
      <div className="relative z-10 flex min-h-screen flex-col justify-between p-10 text-white xl:p-16">
        <Logo size="lg" showText darkSurroundings className="mb-8 shrink-0" to="/" />

        <div className="flex min-h-0 flex-1 flex-col justify-center py-4">
          <h1 className="mb-6 text-4xl font-extrabold leading-[1.12] tracking-tight text-white xl:text-5xl [text-shadow:0_2px_28px_rgba(0,0,0,0.25)]">
            {config.title}
          </h1>
          <p className="mb-10 max-w-md text-base font-medium leading-relaxed text-white/90 [text-shadow:0_1px_16px_rgba(0,0,0,0.2)] xl:text-lg">
            {config.body}
          </p>

          <ul className="max-w-md space-y-4">
            {FEATURES.map(({ Icon, text }) => (
              <li key={text} className="flex items-start gap-3.5 text-sm leading-snug text-white/90 xl:text-[15px]">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15 backdrop-blur-sm">
                  <Icon size={16} className="text-white" strokeWidth={2} />
                </div>
                <span className="pt-1.5">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-white/60">
          <Link to="/privacy" className="transition-colors hover:text-white">
            Privacy
          </Link>
          <Link to="/terms" className="transition-colors hover:text-white">
            Terms
          </Link>
          <span className="text-white/50">&copy; 2026 Yaarvi</span>
        </div>
      </div>
    </div>
  );
};
